<template>
  <div class="modal-overlay">
    <div class="modal">
      <div class="modal-header">
        <h2>{{ $t('bo.platformSkills.createGroup.title') }}</h2>
        <button @click="$emit('close')" class="btn-close">✕</button>
      </div>

      <form @submit.prevent="handleSubmit" class="modal-form">
        <div class="modal-body">
          <div class="field">
            <label>{{ $t('bo.common.name') }} <span class="required">*</span></label>
            <input v-model="form.name" type="text" required :placeholder="$t('bo.platformSkills.createGroup.namePlaceholder')" />
          </div>

          <div class="field">
            <label>{{ $t('bo.common.description') }}</label>
            <textarea v-model="form.description" rows="3" :placeholder="$t('bo.platformSkills.createGroup.descPlaceholder')" />
          </div>

          <!-- Traduction francaise (skill_groups_i18n) : affichee dans l'application en francais. -->
          <div class="translation-box">
            <div class="translation-head">
              <span class="translation-title">{{ $t('bo.platformSkills.translation.title') }}</span>
              
            </div>
            <div class="field">
              <label>{{ $t('bo.platformSkills.translation.nameFr') }}</label>
              <input v-model="translationFr.name" type="text" maxlength="100" />
            </div>
            <div class="field">
              <label>{{ $t('bo.platformSkills.translation.descriptionFr') }}</label>
              <textarea v-model="translationFr.description" rows="2" />
            </div>
            <p class="hint">{{ $t('bo.platformSkills.translation.hintCreateGroup') }}</p>
          </div>

          <div class="field-row">
            <div class="field">
              <label>{{ $t('bo.platformSkills.groupType') }} <span class="required">*</span></label>
              <select v-model="form.group_type" required>
                <option value="domain">{{ $t('bo.platformSkills.domain') }}</option>
                <option value="transversal">{{ $t('bo.platformSkills.transversal') }}</option>
              </select>
            </div>
          </div>

          <div v-if="error" class="error-msg">{{ error }}</div>
        </div>

        <div class="modal-footer">
          <button type="button" @click="$emit('close')" class="btn-cancel">{{ $t('bo.common.cancel') }}</button>
          <button type="submit" class="btn-primary" :disabled="loading">
            {{ loading ? $t('bo.common.creating') : $t('bo.platformSkills.createGroup.submit') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import platformSkillService from '@/services/platformSkillService'

const { t } = useI18n()

const emit = defineEmits(['close', 'created'])

const form = ref({
  name: '',
  description: '',
  group_type: 'domain',
})
// Traduction francaise (facultative) ; la suggestion automatique est proposee a la modification.
const translationFr = ref({ name: '', description: '' })
const loading = ref(false)
const error = ref('')

const handleSubmit = async () => {
  loading.value = true
  error.value = ''
  try {
    await platformSkillService.createGroup({
      ...form.value,
      translations: translationFr.value.name.trim()
        ? { fr: { name: translationFr.value.name.trim(), description: translationFr.value.description.trim() } }
        : undefined,
    })
    emit('created')
  } catch (e) {
    error.value = e.response?.data?.message || t('bo.platformSkills.failedCreateGroup')
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: white;
  border-radius: 0.75rem;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 700;
  color: #1f2937;
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.125rem;
  color: #9ca3af;
  cursor: pointer;
  padding: 0.25rem;
}

.btn-close:hover { color: #374151; }

.modal-form { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
.modal-body {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.field {
  margin-bottom: 1.25rem;
  flex: 1;
}

.field-row {
  display: flex;
  gap: 1rem;
}

.field label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.375rem;
}

.required { color: #ef4444; }

.field input,
.field select,
.field textarea {
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #1f2937;
  background: white;
  box-sizing: border-box;
}

.field input:focus,
.field select:focus,
.field textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59,130,246,0.1);
}

.field textarea { resize: vertical; }

.error-msg {
  padding: 0.75rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 0.375rem;
  color: #dc2626;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  border-top: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.btn-cancel {
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;
}

.btn-cancel:hover { background: #f9fafb; }

.btn-primary {
  padding: 0.5rem 1.25rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.translation-box { border: 1px solid #e5e7eb; border-radius: 0.75rem; padding: 0.9rem 1rem; background: #fafafa; display: flex; flex-direction: column; gap: 0.6rem; }
.translation-head { display: flex; justify-content: space-between; align-items: center; gap: 0.5rem; }
.translation-title { font-weight: 600; color: #1f2937; font-size: 0.9rem; }
.btn-translate { border: 1px solid #3b82f6; color: #3b82f6; background: #fff; border-radius: 0.5rem; padding: 0.35rem 0.8rem; font-size: 0.8rem; cursor: pointer; }
.btn-translate:disabled { opacity: 0.6; cursor: default; }
.translation-box .hint { margin: 0; color: #6b7280; font-size: 0.8rem; }
</style>
